import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts'

interface ContractDistItem {
  type: string
  count: number
  totalSalary: number
}

interface ContractDistributionChartProps {
  data: ContractDistItem[] | undefined
  isLoading: boolean
}

const CONTRACT_COLORS: Record<string, string> = {
  CLT: '#D2FA52',
  PJ: '#4760E0',
  ESTAGIO: '#10B981',
  SOCIO: '#F59E0B',
  FREELANCER: '#EF4444',
  UNKNOWN: '#838998',
}

export function ContractDistributionChart({ data, isLoading }: ContractDistributionChartProps) {
  if (isLoading) {
    return <ChartSkeleton title="Distribuição por Contrato" />
  }

  if (!data || data.length === 0) {
    return <ChartEmpty title="Distribuição por Contrato" />
  }

  const chartData = data.map((item) => ({
    name: item.type,
    value: item.count,
    color: CONTRACT_COLORS[item.type] || CONTRACT_COLORS.UNKNOWN,
  }))

  const total = chartData.reduce((sum, d) => sum + d.value, 0)

  const renderCustomLabel = (entry: { value: number }) => {
    const pct = ((entry.value / total) * 100).toFixed(0)
    return `${pct}%`
  }

  return (
    <div className="bg-brand-dark-light border border-brand-dark-lighter rounded-monumenta p-6 shadow-monumenta">
      <div className="mb-6">
        <h3 className="text-white font-display text-xl font-semibold mb-1">Distribuição por Contrato</h3>
        <p className="text-brand-gray font-body text-sm">Colaboradores ativos por tipo de contrato</p>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomLabel}
            outerRadius={100}
            innerRadius={60}
            dataKey="value"
            paddingAngle={2}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const item = payload[0]
                return (
                  <div
                    style={{
                      backgroundColor: '#24262B',
                      border: '1px solid #2A2D34',
                      borderRadius: '12px',
                      padding: '10px',
                    }}
                  >
                    <p style={{ color: '#ffffff', fontWeight: 600, marginBottom: '5px' }}>
                      {item.name}
                    </p>
                    <p style={{ color: '#D2FA52' }}>
                      {item.value} colaboradores
                    </p>
                  </div>
                )
              }
              return null
            }}
          />
          <Legend
            verticalAlign="bottom"
            height={36}
            formatter={(value: string, entry: any) => (
              <span style={{ fontSize: '13px', color: '#ffffff' }}>
                {value}: {entry.payload.value}
              </span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  )
}

function ChartSkeleton({ title }: { title: string }) {
  return (
    <div className="bg-brand-dark-light border border-brand-dark-lighter rounded-monumenta p-6 animate-pulse shadow-monumenta">
      <div className="mb-6">
        <div className="h-6 w-48 bg-brand-dark-lighter rounded mb-2" />
        <div className="h-4 w-64 bg-brand-dark-lighter rounded" />
      </div>
      <div className="flex items-center justify-center h-[300px]">
        <div className="w-[200px] h-[200px] rounded-full bg-brand-dark-lighter" />
      </div>
    </div>
  )
}

function ChartEmpty({ title }: { title: string }) {
  return (
    <div className="bg-brand-dark-light border border-brand-dark-lighter rounded-monumenta p-6 shadow-monumenta">
      <div className="mb-6">
        <h3 className="text-white font-display text-xl font-semibold mb-1">{title}</h3>
        <p className="text-brand-gray font-body text-sm">Colaboradores ativos por tipo de contrato</p>
      </div>
      <div className="flex items-center justify-center h-[300px]">
        <p className="text-brand-gray font-body text-sm">Nenhum dado disponível</p>
      </div>
    </div>
  )
}
